import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Horizontal row with a header and arrow controls.
 *
 * The arrows only appear when there is somewhere to scroll and the pointer is
 * a mouse; on a phone the row is swiped, so the buttons would just cover
 * artwork.
 */
export default function Carousel({ title, to, action, children, testId }) {
  const scroller = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    measure();
    const el = scroller.current;
    if (!el) return;
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure, children]);

  const nudge = (direction) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  };

  const hasArrows = !(atStart && atEnd);

  return (
    <section className="mb-9" data-testid={testId}>
      <div className="flex items-end justify-between mb-3 gap-4">
        {to ? (
          <Link to={to} className="group flex items-center gap-1.5 min-w-0">
            <h2 className="font-heading text-xl font-bold truncate">{title}</h2>
            <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0" />
          </Link>
        ) : (
          <h2 className="font-heading text-xl font-bold truncate">{title}</h2>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {action}
          {hasArrows && (
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => nudge(-1)}
                disabled={atStart}
                aria-label="Scroll left"
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-white/5 flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => nudge(1)}
                disabled={atEnd}
                aria-label="Scroll right"
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-white/5 flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={scroller}
        className="flex gap-4 overflow-x-auto pb-2 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
    </section>
  );
}
